import React, { useContext, useMemo, useState } from "react";
import useSWR from "swr";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Link,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PlaceIcon from "@mui/icons-material/Place";
import SettingsIcon from "@mui/icons-material/Settings";
import Parser from "html-react-parser";
import Linkify from "linkify-react";
import { UserContextType, useAuth } from "../auth";
import { GroupApiError, groupService } from "../service/GroupService";
import { GroupDetail as GroupDetailModel, GroupEvent } from "../types/group";
import { editorial } from "../common/editorialTheme";
import AddToCalendarButton from "../announcements/AddToCalendarButton";
import GetDirectionsButton from "../announcements/GetDirectionsButton";
import GroupMembershipAction from "./GroupMembershipAction";
import { groupCardSx } from "./Groups";
import { formatEventRange, isUpcoming, stripHtml } from "./groupFormat";

const htmlSx = {
  fontFamily: editorial.bodyFont,
  color: editorial.text,
  "& h1, & h2, & h3": { fontFamily: editorial.headlineFont, color: editorial.primary, fontWeight: 600, mt: 2, mb: 1.5 },
  "& p": { lineHeight: 1.7, mb: 2, mt: 0, color: editorial.textVariant, fontSize: "1.05rem" },
  "& ul, & ol": { mb: 2, pl: 4 },
  "& img": { maxWidth: "100%", height: "auto", borderRadius: "8px", my: 2 },
  "& blockquote": {
    borderLeft: `3px solid ${editorial.primary}`,
    pl: 2,
    py: 1,
    my: 2,
    bgcolor: editorial.sectionAlt,
    fontStyle: "italic",
    color: editorial.primary,
  },
};

const metaSx = {
  fontFamily: editorial.bodyFont,
  color: editorial.secondary,
  fontSize: "0.75rem",
  fontWeight: 500,
  letterSpacing: "0.04em",
  textTransform: "uppercase" as const,
};

const sectionTitleSx = {
  fontFamily: editorial.headlineFont,
  fontWeight: 600,
  color: editorial.primary,
  fontSize: "1.25rem",
};

const EventItem: React.FC<{ event: GroupEvent; past?: boolean }> = ({ event, past }) => (
  <Box sx={{ py: 2.5, borderBottom: `1px solid ${editorial.divider}`, "&:last-child": { borderBottom: "none" }, opacity: past ? 0.7 : 1 }}>
    <Typography sx={{ fontFamily: editorial.bodyFont, fontWeight: 600, color: editorial.text, fontSize: "1.05rem" }}>
      {event.title}
    </Typography>
    <Typography sx={{ ...metaSx, mt: 0.5 }}>{formatEventRange(event)}</Typography>
    {event.location && (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1, color: editorial.textVariant }}>
        <PlaceIcon fontSize="small" />
        <Typography sx={{ fontFamily: editorial.bodyFont, fontSize: "0.95rem" }}>{event.location}</Typography>
      </Box>
    )}
    {event.description && (
      <Typography
        sx={{ fontFamily: editorial.bodyFont, color: editorial.textVariant, mt: 1, whiteSpace: "pre-line", lineHeight: 1.6 }}
      >
        <Linkify options={{ target: "_blank" }}>{event.description}</Linkify>
      </Typography>
    )}
    {!past && (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
        <AddToCalendarButton
          event={{
            title: event.title,
            description: stripHtml(event.description) || undefined,
            location: event.location || undefined,
            startDateTime: event.startDateTime,
            endDateTime: event.endDateTime,
          }}
        />
        {event.location && <GetDirectionsButton location={event.location} />}
      </Box>
    )}
  </Box>
);

const GroupDetail: React.FC = () => {
  const { id } = useParams<string>();
  const { getToken } = useContext<UserContextType>(useAuth);
  const token = getToken ? getToken() : "";
  const [tab, setTab] = useState(0);

  const { data, error, isLoading, mutate } = useSWR<GroupDetailModel>(
    id ? ["group", id, token] : null,
    () => groupService.getGroup(id!, token || undefined),
    { shouldRetryOnError: false }
  );

  const updates = useMemo(
    () =>
      data?.updates
        ? [...data.updates].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        : null,
    [data]
  );

  const { upcomingEvents, pastEvents } = useMemo(() => {
    const events = data?.events ?? [];
    const byStart = (a: GroupEvent, b: GroupEvent) =>
      new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime();
    return {
      upcomingEvents: events.filter(isUpcoming).sort(byStart),
      pastEvents: events.filter((e) => !isUpcoming(e)).sort((a, b) => byStart(b, a)),
    };
  }, [data]);

  const notFound = error instanceof GroupApiError && error.status === 404;

  if (error) {
    return (
      <Box sx={{ backgroundColor: editorial.canvas, minHeight: "100vh", pt: { xs: 4, md: 6 } }}>
        <Container maxWidth="md">
          {notFound ? (
            <Box sx={groupCardSx}>
              <Typography component="h1" sx={{ ...sectionTitleSx, fontSize: "1.75rem", mb: 1 }}>
                Group not found
              </Typography>
              <Typography sx={{ fontFamily: editorial.bodyFont, color: editorial.textVariant, mb: 2 }}>
                This group doesn't exist or isn't open right now.
              </Typography>
              <Button component={RouterLink} to="/groups" variant="outlined">
                See all groups
              </Button>
            </Box>
          ) : (
            <Alert severity="error">Failed to load this group.</Alert>
          )}
        </Container>
      </Box>
    );
  }

  if (isLoading || !data) {
    return (
      <Box sx={{ backgroundColor: editorial.canvas, minHeight: "100vh", pt: { xs: 4, md: 6 }, pb: 10 }}>
        <Container maxWidth="md">
          <Skeleton variant="rectangular" height={140} sx={{ mb: 3, borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
        </Container>
      </Box>
    );
  }

  const canSeeContent = updates !== null || data.events !== null;

  return (
    <Box sx={{ backgroundColor: editorial.canvas, minHeight: "100vh", pb: 10 }}>
      <Container maxWidth="md" sx={{ pt: { xs: 4, md: 6 } }}>
        {/* Header */}
        <Box sx={{ ...groupCardSx, mb: 4 }}>
          <Breadcrumbs
            separator="/"
            sx={{
              mb: 1.5,
              fontFamily: editorial.bodyFont,
              fontSize: "0.8rem",
              color: editorial.secondary,
              "& .MuiBreadcrumbs-separator": { color: editorial.divider },
            }}
          >
            <Link component={RouterLink} to="/groups" underline="hover" sx={{ color: editorial.secondary }}>
              Groups
            </Link>
            <Typography sx={{ fontFamily: editorial.bodyFont, fontSize: "0.8rem", color: editorial.text, fontWeight: 600 }}>
              {data.name}
            </Typography>
          </Breadcrumbs>

          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: editorial.headlineFont,
                fontWeight: 600,
                color: editorial.primary,
                fontSize: { xs: "2rem", md: "2.75rem" },
                lineHeight: 1.15,
                letterSpacing: "-0.015em",
              }}
            >
              {data.name}
            </Typography>
            {data.canManage && (
              <Button
                component={RouterLink}
                to={`/admin/groups/${data.id}`}
                variant="outlined"
                size="small"
                startIcon={<SettingsIcon />}
              >
                Manage
              </Button>
            )}
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2, color: editorial.secondary }}>
            {data.meetingInfo && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <ScheduleIcon fontSize="small" />
                <Typography sx={{ fontFamily: editorial.bodyFont, fontSize: "0.95rem" }}>{data.meetingInfo}</Typography>
              </Box>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <PeopleIcon fontSize="small" />
              <Typography sx={{ fontFamily: editorial.bodyFont, fontSize: "0.95rem" }}>
                {data.memberCount} {data.memberCount === 1 ? "member" : "members"}
              </Typography>
            </Box>
          </Box>

          {!data.isActive && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This group is inactive, so only its leaders and administrators can see it.
            </Alert>
          )}

          <Box sx={{ mt: 2.5 }}>
            <GroupMembershipAction group={data} onChanged={() => mutate()} />
          </Box>
        </Box>

        {/* Description */}
        <Box sx={{ ...groupCardSx, mb: 4, ...htmlSx }}>
          <Linkify options={{ target: "_blank" }}>{Parser(data.description || "")}</Linkify>
        </Box>

        {canSeeContent ? (
          <Box sx={{ ...groupCardSx, pt: { xs: 1, md: 2 } }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="fullWidth"
              sx={{ borderBottom: `1px solid ${editorial.divider}`, mb: 1 }}
            >
              <Tab label={`Updates${updates ? ` (${updates.length})` : ""}`} />
              <Tab label={`Events (${upcomingEvents.length})`} />
            </Tabs>

            {tab === 0 && (
              <Box>
                {!updates || updates.length === 0 ? (
                  <Typography sx={{ fontFamily: editorial.bodyFont, color: editorial.textVariant, py: 3 }}>
                    No updates yet.
                  </Typography>
                ) : (
                  updates.map((update) => (
                    <Box
                      key={update.id}
                      sx={{ py: 2.5, borderBottom: `1px solid ${editorial.divider}`, "&:last-child": { borderBottom: "none" } }}
                    >
                      <Typography sx={{ fontFamily: editorial.bodyFont, fontWeight: 600, color: editorial.text, fontSize: "1.05rem" }}>
                        {update.title}
                      </Typography>
                      <Typography sx={{ ...metaSx, mt: 0.5, mb: 1 }}>
                        {new Date(update.createdAt).toLocaleString()}
                        {update.createdBy ? ` · ${update.createdBy}` : ""}
                      </Typography>
                      <Box sx={{ ...htmlSx, "& p": { lineHeight: 1.7, color: editorial.textVariant, m: 0, mb: 1 } }}>
                        <Linkify options={{ target: "_blank" }}>{Parser(update.body || "")}</Linkify>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            )}

            {tab === 1 && (
              <Box>
                {upcomingEvents.length === 0 && (
                  <Typography sx={{ fontFamily: editorial.bodyFont, color: editorial.textVariant, py: 3 }}>
                    No upcoming events.
                  </Typography>
                )}
                {upcomingEvents.map((event) => (
                  <EventItem key={event.id} event={event} />
                ))}
                {pastEvents.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography sx={{ ...sectionTitleSx, fontSize: "1rem", mb: 0.5 }}>Past events</Typography>
                    {pastEvents.map((event) => (
                      <EventItem key={event.id} event={event} past />
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        ) : (
          data.membershipStatus !== "Pending" && (
            <Box sx={{ ...groupCardSx, textAlign: "center" }}>
              <Typography sx={{ ...sectionTitleSx, mb: 1 }}>Want to be part of {data.name}?</Typography>
              <Typography sx={{ fontFamily: editorial.bodyFont, color: editorial.textVariant, mb: 2 }}>
                Members see the group's updates and upcoming events. Send a request and a group leader will get back to you.
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <GroupMembershipAction group={data} onChanged={() => mutate()} />
              </Box>
            </Box>
          )
        )}
      </Container>
    </Box>
  );
};

export default GroupDetail;
